import { useCallback, useEffect, useRef, useState } from 'react';
/*
 * **The two relative imports below carry `.js` and the rest of this package's do not.**
 *
 * That is not a slip. This is the second component in the repository that `npm test` compiles
 * and runs — `chat/Prose.tsx` is the first — and the test runs the emitted ESM in plain Node,
 * which resolves specifiers literally: an extensionless `'./i18n'` is `ERR_MODULE_NOT_FOUND`
 * at runtime even though it type-checks and bundles. `Prose.tsx` never hit this because it has
 * no relative imports at all. TypeScript maps `./i18n.js` back to `i18n.ts` and Vite does the
 * same, so the app is unaffected; what this buys is the only check in the repository on what
 * this panel actually puts on the page for a learner who asked for less motion.
 */
import { useI18n, type CopyKey } from './i18n.js';
import {
  START,
  autoAdvances,
  dwellMs,
  liveness,
  nextCard,
  positionOf,
  stepCard,
  type Card,
  type Rotation,
} from './plan/carousel.js';
/* One set of words for a source count, shared with the research section below this panel — see
   `sourceCount`. The `.js` extension is this file's own import rule, explained above. */
import { sourceCount } from './plan/subject.js';

/**
 * The looping carousel on the plan's researching face. F-141.
 *
 * **This is the engagement half of the feature and it is a display, not a plan.** Leo:
 * *"the objective is not to make the user act on an incomplete plan, rather to demonstrate to
 * the user, in a valuable way, that research is ongoing and maintain engagement."* So there is
 * no placeholder node here, nothing partial to act on and nothing to answer — every card is
 * text a pass already wrote, shown one at a time, and it loops until the run finishes.
 *
 * All the judgement lives in `plan/carousel.ts`, where a Node test can see it: what is in the
 * deck, what comes next, how long a card stays, whether it moves at all, and what a screen
 * reader is told. `npm test` does not type-check `.tsx`, so anything decided in this file is
 * decided where nothing can check it. What is left here is the markup and the three browser
 * facts a module cannot ask for — the motion preference, whether the tab is visible, and
 * whether a pointer or a keyboard is in the panel.
 */

/** Anything of this kind carries the subject's own label rather than a generic one. */
const KIND_COPY: Record<Card['kind'], CopyKey> = {
  outline: 'carousel.kind.outline',
  promise: 'carousel.kind.promise',
  finding: 'carousel.kind.finding',
  /* `gap: 'carousel.kind.gap'` was the fourth entry until F-158. See `CardKind` in
     `plan/carousel.ts`: the text under that label was written for whoever builds the curriculum,
     not for the learner reading it. */
};

/**
 * Whether this browser has been asked for less motion.
 *
 * Live rather than read once: the preference can change while the page is open — a phone
 * entering low-power mode does it — and a carousel that kept rotating because it read the
 * setting at mount is the same failure as never having read it.
 *
 * Defaults to *reduced* when `matchMedia` is missing. Every way of not knowing resolves towards
 * not moving, which is the direction where being wrong costs a learner a manual button press
 * rather than a screen that moves at somebody who gets sick from it.
 */
function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (): void => setReduced(query.matches);
    query.addEventListener('change', onChange);
    onChange();
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/**
 * Whether anybody is looking at this tab.
 *
 * See `autoAdvances`: this is a correctness rule and not an optimisation. A background tab that
 * went on rotating would walk the whole deck while nobody was there, and the learner coming
 * back — the person this feature exists for — would find nothing left unseen.
 */
function useHidden(): boolean {
  const [hidden, setHidden] = useState(
    () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
  );
  useEffect(() => {
    const onChange = (): void => setHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return hidden;
}

export default function ResearchCarousel({
  objectiveId,
  deck,
}: {
  objectiveId: string;
  /**
   * What there is to show, built and gated by the panel above.
   *
   * **Handed down rather than built here, and `carouselShows` is asked there rather than here.**
   * A review caught what the other arrangement cost: the decision to stop was an early return
   * *after* every hook, so a finished run rendered nothing and went on scheduling a timer every
   * three to nine seconds for as long as the plan screen was open — and that screen is where a
   * learner sits after a run, under the offer to build. Nothing threw and nothing logged; it was
   * a phone waking up for ever. Deciding in the parent means React unmounts this, which clears
   * the timer because that is what unmounting does.
   *
   * It also removes the second `deckOf` a poll: the panel needed the deck anyway, to know
   * whether to show its own quotations.
   */
  deck: readonly Card[];
}) {
  const { t, locale } = useI18n();
  const reducedMotion = useReducedMotion();
  const hidden = useHidden();
  const [stopped, setStopped] = useState(false);
  const [touched, setTouched] = useState(false);
  const [rotation, setRotation] = useState<Rotation>(START);

  /*
   * The deck as it is *now*, for a timer that must not be rebuilt when it changes.
   *
   * The progress endpoint answers every five seconds and a card may stay up for nine, so an
   * effect that depended on `deck` would be torn down and restarted before it ever fired: the
   * carousel would sit on its first card for the whole run, moving only when a poll happened to
   * be slower than the dwell. That is a silent failure of exactly this project's kind — nothing
   * throws, nothing logs, and what a learner sees is a panel that does not move.
   *
   * Written in an effect rather than in the render body, which is where it was until a review
   * pointed at React's own rule: a render that is thrown away still runs its body, so a ref
   * assigned there can be left holding a deck that was never committed.
   */
  const deckNow = useRef(deck);
  useEffect(() => {
    deckNow.current = deck;
  });

  const paused = stopped || touched;
  const auto = autoAdvances({ reducedMotion, paused, hidden });

  /*
   * The card showing, and the one that stands in for it before anything has been chosen.
   *
   * **The stand-in is not cosmetic.** Choosing the first card is an effect, and an effect runs
   * after the paint — so without this the panel renders as nothing on the frame it appears, and
   * a learner arriving at the researching face sees the layout jump. `nextCard` picks the same
   * first card a moment later, so the stand-in is what it is about to choose rather than a
   * different card that then flicks away.
   */
  const chosen = deck.find((entry) => entry.key === rotation.key);
  const card = chosen ?? deck[0];
  const dwell = dwellMs(card);

  /* A new objective is a new deck and a new memory of what was shown. */
  useEffect(() => {
    setRotation(START);
  }, [objectiveId]);

  /*
   * Something has to be on screen before the first advance, and the card showing may also have
   * left the deck — a subject re-researched between two polls has different text and so a
   * different key. Both are "nothing is showing", and both are answered by choosing one.
   */
  useEffect(() => {
    if (deck.length === 0 || chosen) return;
    setRotation((from) => nextCard(deckNow.current, { seen: from.seen }));
  }, [deck, chosen]);

  /*
   * `rotation` and not `rotation.key`, which is the one-card stall a review found.
   *
   * A deck of one — three subjects of which two have nothing long enough to show — makes
   * `nextCard` return the same key it was given. Keyed on the string, none of the three
   * dependencies would change, the effect would not re-run, and the timer that had just fired
   * would never be replaced: the carousel stops, silently, for the rest of the run. `nextCard`
   * and `stepCard` always return a fresh object, so the identity is a sound trigger and a
   * repeated key restarts the dwell rather than ending it.
   */
  useEffect(() => {
    if (!auto) return;
    const timer = setTimeout(() => setRotation((from) => nextCard(deckNow.current, from)), dwell);
    return () => clearTimeout(timer);
  }, [auto, dwell, rotation]);

  const step = useCallback((delta: number) => {
    setRotation((from) => stepCard(deckNow.current, from, delta));
  }, []);

  // An empty deck has nothing to draw. Whether there should be a carousel at all is the panel
  // above's question, asked through `carouselShows` before this is rendered at all.
  if (!card) return null;

  // From the card actually on screen, so the counter is right on the stand-in frame too.
  const place = chosen ? positionOf(deck, rotation) : 1;
  /* What the three controls name with `aria-controls`, so a reader can say which region each of
     them moves. Derived from the objective rather than generated, because two objectives' panels
     are never on screen together and a stable id survives every re-render. */
  const cardId = `carousel-card-${objectiveId}`;
  const from = t('carousel.from')
    .replace('{title}', card.title)
    .replace('{place}', String(card.place))
    .replace('{subjects}', String(card.subjects));
  /*
   * What this subject's research rests on, in sources (F-158).
   *
   * **It said characters until F-158, and the count was the complaint**: *"number of characters
   * is quite irrelevant, can we do number of sources?"* `typeof` rather than truthiness, so a
   * subject that read nought sources says so instead of silently saying nothing — and a card
   * whose row predates the counter carries no `sources` at all and gets no clause, rather than
   * claiming a nought nobody measured. Which of the four sentences to use is decided in
   * `sourceCount` below, on the same keys `plan/subject.ts` picks for the research screen, so
   * the two places a learner reads a source count cannot phrase it differently.
   */
  const sources = sourceCount(card.sources);
  const found = sources
    ? t(sources.key).replace('{sources}', sources.count.toLocaleString(locale))
    : '';

  return (
    /*
     * `aria-roledescription="carousel"` on a labelled `section`, which is the APG pattern.
     *
     * The pause is on pointer and on focus alike, and the second is not an afterthought: a
     * learner moving through this with a keyboard has no pointer to hover with, and a carousel
     * that rotated out from under the button they had just tabbed to would be unusable by
     * exactly the people the controls are for.
     */
    <section
      className="carousel"
      aria-roledescription={t('carousel.roledescription')}
      aria-label={t('carousel.label')}
      /*
       * A pointer that can hover pauses it; a finger does not, and that is the phone rule.
       *
       * `pointerenter` fires on a tap and `pointerleave` does not fire until something else is
       * tapped, so treating touch as hover would stop the carousel for good the first time
       * somebody's thumb brushed the panel — on the device most of these learners are using.
       * A touch learner pauses with the button, which is why the button is there.
       */
      onPointerEnter={(event) => {
        if (event.pointerType !== 'touch') setTouched(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType !== 'touch') setTouched(false);
      }}
      onFocus={() => setTouched(true)}
      /*
       * Only when focus actually leaves the panel.
       *
       * React's `onBlur` is `focusout`, which bubbles — so tabbing from the previous-card button
       * to the pause button fires it, and a bare `setTouched(false)` would resume the rotation
       * for the instant before the matching focus event, restarting the dwell timer under
       * somebody who is in the middle of operating this by keyboard.
       */
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setTouched(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          step(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          step(1);
        }
      }}
    >
      {/*
        `aria-live` is `off` while this rotates by itself and `polite` the moment the learner is
        the one moving it. See `liveness`: a region that fired every three seconds would
        interrupt whatever a non-sighted learner was reading, thirty-six times a minute, with
        text nobody asked for.
      */}
      <div
        id={cardId}
        className="carousel-card"
        aria-live={liveness({ reducedMotion, paused })}
        aria-atomic="true"
      >
        <p className="carousel-kind">{t(KIND_COPY[card.kind])}</p>
        {/* Keyed on the card so React replaces the node rather than mutating its text, which is
            what lets the fade below run at all — and what makes a screen reader treat it as new
            content when the region is live. */}
        <p className="carousel-text" key={card.key}>
          {card.text}
        </p>
        <p className="carousel-from">
          {from}
          {found ? ` · ${found}` : ''}
        </p>
      </div>

      <div className="carousel-controls">
        <button
          type="button"
          className="carousel-button"
          onClick={() => step(-1)}
          aria-controls={cardId}
          aria-label={t('carousel.prev')}
        >
          ‹
        </button>
        {/*
          Hidden under a reduced-motion preference rather than disabled, because there is nothing
          to pause: the carousel is already not moving, and a pause button on a still panel is a
          control that does nothing and says the setting was not understood.
        */}
        {!reducedMotion && (
          <button
            type="button"
            className={`carousel-button${stopped ? ' carousel-button-on' : ''}`}
            onClick={() => setStopped((was) => !was)}
            aria-controls={cardId}
            /*
             * The label changes and there is no `aria-pressed`, which is APG's rule and not a
             * preference: carrying both makes a reader announce "Start again, toggle button,
             * pressed" — the label naming one state and the pressed bit naming the other. The
             * class below is what the styling keys on, for the same reason.
             */
            aria-label={t(stopped ? 'carousel.resume' : 'carousel.pause')}
          >
            {stopped ? '▶' : '❚❚'}
          </button>
        )}
        <button
          type="button"
          className="carousel-button"
          onClick={() => step(1)}
          aria-controls={cardId}
          aria-label={t('carousel.next')}
        >
          ›
        </button>
        {/*
          Where this is in the deck, in text.

          The bar beside it is the only thing on screen that moves for a reason other than new
          material arriving, so it is drawn only while the carousel is actually advancing — and
          the count is always there, which is what a learner who cannot see the bar reads
          instead. It is outside the live region rather than hidden from it: a learner driving
          this by hand wants to know where they are and can read it whenever they like — what it
          must not do is be read out on every card, which is what putting it inside the region
          would have done.
        */}
        <span className="carousel-count">
          {t('carousel.position')
            .replace('{place}', String(place))
            .replace('{total}', String(deck.length))}
        </span>
      </div>

      {auto && (
        <div className="carousel-track" aria-hidden="true">
          <span
            className="carousel-track-fill"
            key={card.key}
            style={{ animationDuration: `${dwell}ms` }}
          />
        </div>
      )}
    </section>
  );
}
